<template>
  <div class="data-table">
    <b-table
      v-bind="$attrs"
      v-on="$listeners"
      :items="items"
      :fields="fields"
      :busy="busy"
      :primary-key="primaryKey"
      :tbody-tr-class="tbodyTrClass"
      :show-empty="showEmpty"
      responsive
      borderless
    >
      <template #table-busy>
        <slot name="table-busy">
          <div class="text-center py-3">{{ busyText }}</div>
        </slot>
      </template>

      <template #empty>
        <slot name="empty">
          <div class="text-center py-3">{{ emptyText }}</div>
        </slot>
      </template>

      <template
        v-for="slotName in forwardedSlotNames"
        v-slot:[slotName]="slotProps"
      >
        <slot :name="slotName" v-bind="slotProps" />
      </template>
    </b-table>
  </div>
</template>

<script>
/**
 * DataTable
 *
 * Generic, reusable table for any context, with the same "card rows"
 * look as the other `components/table` components (rounded corners,
 * spacing between rows, highlighted hover). Wraps BootstrapVue's
 * `<b-table>` without knowing the domain of who uses it — all
 * customization is done through `fields` + slots.
 *
 * Content customization:
 * - `#cell(<key>)="{ item, ... }"`: content for a specific cell, where
 *   `<key>` is the `key` defined in `fields` (same API as `b-table`).
 *   Any named slot passed to this component is automatically forwarded
 *   to the inner `b-table`, so just declare the slots that make sense
 *   for your `fields` — none need to be registered here.
 * - `#table-busy`: custom content for the loading state (otherwise it
 *   uses the `busyText` text).
 * - `#empty`: custom content for when `items` is empty (otherwise it
 *   uses the `emptyText` text).
 *
 * Any other `b-table` prop/event (e.g. `sort-by`, `filter`,
 * `@row-clicked`) can be passed directly to `<DataTable>` — they are
 * forwarded through `$attrs`/`$listeners` without being declared here.
 */
export default {
  name: "DataTable",

  inheritAttrs: false,

  props: {
    /** List of items (rows) to display. */
    items: {
      type: Array,
      default: () => [],
    },
    /**
     * Column definitions, same format as `b-table` `fields`
     * (https://bootstrap-vue.org/docs/components/table#fields-column-definitions):
     * `{ key, label, thClass, tdClass, ... }`.
     */
    fields: {
      type: Array,
      required: true,
    },
    /** Shows the loading state (`#table-busy` slot) while `true`. */
    busy: {
      type: Boolean,
      default: false,
    },
    /** Unique field used as the key for each row (`b-table` `primary-key`). */
    primaryKey: {
      type: String,
      default: "id",
    },
    /** Default text for the loading state, used when `#table-busy` is not passed. */
    busyText: {
      type: String,
      default: "Carregando",
    },
    /** Default text for the empty state, used when `#empty` is not passed. */
    emptyText: {
      type: String,
      default: "Nenhum resultado encontrado",
    },
    /** Shows the empty state (`#empty` slot) when `items` is empty. */
    showEmpty: {
      type: Boolean,
      default: true,
    },
    /**
     * Extra class(es) applied to the data rows (`<tr>`). Accepts a fixed
     * string/array or a function `(item, type) => string` for dynamic
     * per-row styles (e.g. highlighting overdue deadlines).
     */
    rowClass: {
      type: [String, Array, Function],
      default: null,
    },
    /** Adds a pointer cursor to the rows (use when rows are clickable). */
    clickable: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    forwardedSlotNames() {
      return Object.keys(this.$scopedSlots).filter(
        (name) => !["table-busy", "empty"].includes(name)
      );
    },

    tbodyTrClass() {
      return (item, type) => {
        const classes = ["card-table-row"];

        if (this.clickable) {
          classes.push("clickable");
        }

        if (typeof this.rowClass === "function") {
          const extra = this.rowClass(item, type);

          if (Array.isArray(extra)) {
            classes.push(...extra);
          } else if (extra) {
            classes.push(extra);
          }
        } else if (Array.isArray(this.rowClass)) {
          classes.push(...this.rowClass);
        } else if (this.rowClass) {
          classes.push(this.rowClass);
        }

        return classes;
      };
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/css/variables.scss";

.data-table {
  max-width: 1400px;
  margin: 0 auto;

  ::v-deep table {
    border-collapse: separate;
    border-spacing: 0 12px;
  }

  ::v-deep thead th {
    border: none;
    text-align: left;
    font-size: 15px;
    font-weight: 700;
    color: #2b2b3a;
    padding: 0 16px 8px 16px;
    white-space: nowrap;
  }

  ::v-deep tbody tr.card-table-row td {
    background-color: $white;
    padding: 18px 16px;
    vertical-align: middle;
    text-align: left;
    font-size: 15px;
    border-top: 1px solid #e7e8ec;
    border-bottom: 1px solid #e7e8ec;

    p {
      margin: 0;
    }

    &:first-child {
      border-left: 1px solid #e7e8ec;
      border-top-left-radius: 12px;
      border-bottom-left-radius: 12px;
      overflow: hidden;
    }

    &:last-child {
      border-right: 1px solid #e7e8ec;
      border-top-right-radius: 12px;
      border-bottom-right-radius: 12px;
      overflow: hidden;
    }
  }

  ::v-deep tbody tr.card-table-row:hover td {
    border-color: $purple !important;
  }

  ::v-deep tbody tr.card-table-row.clickable {
    cursor: pointer;
  }

  /* Row states — applied via the `row-class` prop (e.g. deadlines).
     Kept subtle and modern: white card preserved, soft tinted
     background and a 4px accent bar on the row's left edge. */
  ::v-deep tbody tr.row-warning td,
  ::v-deep tbody tr.row-warning:hover td {
    background-color: rgba($orange2, 0.08) !important;

    &:first-child {
      border-left: 4px solid $orange2 !important;
    }
  }

  ::v-deep tbody tr.row-danger td,
  ::v-deep tbody tr.row-danger:hover td {
    background-color: rgba($red, 0.07) !important;

    &:first-child {
      border-left: 4px solid $red !important;
    }
  }

  /* Cell content — utility classes available to any table using this
     component (same look as the prototype). */
  ::v-deep .nome {
    font-weight: 600;
    color: #2b2b3a;
    min-width: 200px;
  }

  ::v-deep .area {
    color: #4a4a5a;
    max-width: 260px;
    line-height: 1.4;
  }

  ::v-deep .badge {
    display: inline-block;
    padding: 6px 16px;
    border-radius: 6px;
    color: $white;
    font-weight: 700;
    font-size: 14px;
    text-align: center;
  }

  ::v-deep .badge-ciclo {
    background: #5fc2a8;
  }

  ::v-deep .notif-group {
    display: flex;
    gap: 8px;
  }

  ::v-deep .notif-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 8px;
    padding: 6px 12px;
    min-width: 56px;
    color: $white;
    font-size: 12px;
    font-weight: 600;
    line-height: 1.3;

    .valor {
      font-size: 16px;
      font-weight: 800;
      margin-top: 2px;
    }
  }

  ::v-deep .notif-total {
    background: #9b2f6b;
  }

  ::v-deep .notif-pend {
    background: #e6b800;
  }

  ::v-deep .notif-resolv {
    background: #1f9e6d;
  }

  ::v-deep .aval-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }

  ::v-deep .aval-labels {
    display: flex;
    gap: 40px;
    font-size: 11px;
    font-weight: 700;
    color: #4a4a5a;
    padding-left: 2px;
  }

  ::v-deep .aval-group {
    display: flex;
    gap: 6px;
  }

  ::v-deep .aval-box {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    padding: 4px 10px;
    min-width: 44px;
    color: $white;
    font-size: 11px;
    font-weight: 600;
    line-height: 1.3;

    .valor {
      font-size: 14px;
      font-weight: 800;
      margin-top: 1px;
    }
  }

  ::v-deep .aval-ind {
    background: #5fc2a8;
  }

  ::v-deep .aval-colet {
    background: #e8654b;
  }

  ::v-deep .acoes {
    display: flex;
    gap: 16px;
    align-items: center;
    justify-content: center;
  }

  /* Centers cell content (e.g. checkboxes) both horizontally and vertically */
  ::v-deep td.cell-center {
    text-align: center;

    .custom-control {
      display: flex;
      justify-content: center;
    }
  }

  ::v-deep .icon-btn {
    background: none;
    border: none;
    cursor: pointer;
    color: #e8654b;
    font-size: 18px;
    padding: 4px;
    line-height: 1;
  }
}
</style>
